import { AppDate } from '../envTypes';

import { ModelBase, Platform } from './base';
import { Profile } from './profile';
import { StoreFront } from './store';

export type CouponCreateMethod = 'sellerProvide' | 'popshopProvide';

export enum CouponType {
  discountPercent = 'discountPercent',
  discountAmount = 'discountAmount',
  freeShipping = 'freeShipping',
}

export interface BaseCoupon {
  platform?: Platform,
  code: string;
  name?: string;
  imageUrl?: string;
  pagUrl?: string;
  description?: string;
  enabled: boolean;
  createdAt: AppDate;
  quantityLimit: boolean;
  quantity?: number | null;
  redeemedQuantity: number;
  startedAt: AppDate;
  expiredAt: AppDate;
  storeIds?: string[];
  store?: StoreFront;
  couponCreateMethod?: CouponCreateMethod;
  onlyPurchasedUser?: boolean;
  onlyNewUser?: boolean;
  onlyNewUserPerStore?: boolean;
  onlyReactivatedUser?: boolean;
  // when true, storeIds is mandatory
  onlyFollower?: boolean;
  // when true, order.shippingMethod must be popshopProvide
  onlyPopshopShipping?: boolean;
  // when onlyAssignToUser.enabled is true, the coupon could only be assigned to user by seller
  onlyAssignToUser?: {
    enabled: boolean;
    userIds: string[];
  };
  onlyApplyToListings?: {
    listingIds: string[];
  },
  userGroupIds?: string[];
  oncePerStore?: boolean;
  sellerGroupIds?: string[];
  followSelectedSellers?: boolean;
  liveStreamIds?: string[];
  // true means this coupon will not display in available coupon list
  visible?: boolean;
  // true, the coupon cannot be used with other coupons
  exclusive?: boolean;
  createdBy?: {
    profileId: string;
    // some admin roles do not have profile record
    profile?: Profile;
  };
  lastUpdatedBy?: {
    profileId: string;
    // some admin roles do not have profile record
    profile?: Profile;
  };
}

export enum ApplicableOrderType {
  OrderGroup = 'OrderGroup',
  // also applies to master order as master order is a special type of child order
  ChildOrder = 'ChildOrder',
}

export interface PercentCoupon extends BaseCoupon {
  type: CouponType.discountPercent;
  discountPercent: number; // 0-100
  maximumDiscount?: number;

  excludedDropshipPartnerIds?: number[];
  applicableOrderType?: ApplicableOrderType;
}

export interface AmountCoupon extends BaseCoupon {
  type: CouponType.discountAmount;
  discountAmount: number; // 5 = $5.00
  minimalPurchase?: number;
}

export interface FreeShippingCoupon extends BaseCoupon {
  type: CouponType.freeShipping;
  minimalPurchase?: number;
  maximumFreeShippingAmount?: number;
  /**
   * - when applied to legacy shippo orders, this is 5.99
   * - when applied to shippo platform orders, this cannot be greater than actual remaining shipping price
   */
  sellerShippingPriceSubsidy?: number;
}

export type Coupon = PercentCoupon | AmountCoupon | FreeShippingCoupon;

export interface RedeemedCoupon {
  couponId: string;
  coupon: Coupon;
  buyerId: string;
  buyerProfile: Profile;
  createdAt: AppDate;
  usedAt?: AppDate;
  used: boolean;
  orderId?: string;
  targetSellerId?: string;
}

// /coupons/{couponId}/redeemedCoupons/{redeemedCouponId}
export interface CouponRedeemed extends ModelBase {
  buyerId: string;
  buyerProfile: Profile;
  createdAt: AppDate;
  usedAt?: AppDate;
  used: boolean;
  orderId?: string;
  totalRedeemed: number;
}
